import { Dispatch } from "redux"
import { IGetSbCampaignsBody } from "../../types/sbCampaignApi.types"
import { SponsoredBrandsAction, SponsoredBrandsActionType } from "../types/sponsoredBrands"
import sbCampaignApi from "../../api/sbCampaign.api"
import sbTargetApi from "../../api/sbTarget.api"
import { IGetSbTargetBody } from "../../types/sbTargetApi.types"
import { ISbTargetDataForTable } from "../../types/sbTarget.types"
import type { RootState } from "../../../../redux/reducers"

type GetState = () => RootState

const fetchSponsoredBrandsCampaigns = (data: IGetSbCampaignsBody) => {
  return async (dispatch: Dispatch<SponsoredBrandsAction>) => {
    try {
      dispatch({ type: SponsoredBrandsActionType.FETCH_SPONSORED_BRANDS_CAMPAIGNS })
      const sponsoredProductData = await sbCampaignApi.getSbCampaigns(data)

      dispatch({
        type: SponsoredBrandsActionType.FETCH_SPONSORED_BRANDS_CAMPAIGNS_SUCCESS,
        payload: sponsoredProductData.data,
      })
    } catch (e) {
      dispatch({
        type: SponsoredBrandsActionType.FETCH_SPONSORED_BRANDS_CAMPAIGNS_ERROR,
        payload: "sponsored brands load error",
      })
    }
  }
}

const TARGETS_REQUEST_CHUNK_SIZE = 50

function chunkCampaignIds(campaignIds: string[]): string[][] {
  const chunks: string[][] = []
  for (let start = 0; start < campaignIds.length; start += TARGETS_REQUEST_CHUNK_SIZE) {
    chunks.push(campaignIds.slice(start, start + TARGETS_REQUEST_CHUNK_SIZE))
  }
  return chunks
}

function readRequestGeneration(getState: GetState): number {
  return getState().sponsoredBrandsTarget.requestGeneration
}

/**
 * R2 — strict "at most one request per campaign": drop ids that are already
 * in flight (`pendingCampaignIds`) and duplicates within the same call, BEFORE
 * marking anything pending or building chunks. A single-row expand followed by
 * Expand All while that row's request is outstanding must not request the row
 * again — and because every pending id is then owned by exactly one request,
 * each response clears only the markers it set (re-review 2026-09-25).
 */
export interface IFetchTargetsOptions {
  /** Explicit user Retry: failed campaigns are requested again. Never set by automatic/bulk fetches. */
  retryFailed?: boolean
}

function selectCampaignIdsToRequest(
  getState: GetState,
  campaignIds: string[],
  options: IFetchTargetsOptions = {}
): string[] {
  const { pendingCampaignIds, targetData, failedCampaignIds } = getState().sponsoredBrandsTarget
  const pending = new Set((pendingCampaignIds ?? []).map(String))
  // Already LOADED for this mode/date range (the SUCCESS upsert stores an entry
  // for every requested id, including empty results): never re-request them
  // automatically — Expand All after a single-row expand must not fetch that
  // row again (re-review #2, 2026-09-25). A mode/date change resets the
  // slice (RESET), which is what makes a fresh request legitimate.
  const loaded = new Set((targetData ?? []).map((target) => String(target.campaignId)))
  // FAILED campaigns are excluded from ordinary/bulk fetches too (re-review #3):
  // one failure = one request per campaign/mode/range. Only the explicit Retry
  // button (options.retryFailed) may request them again.
  const failed = options.retryFailed
    ? new Set<string>()
    : new Set((failedCampaignIds ?? []).map(String))
  const seen = new Set<string>()
  const toRequest: string[] = []
  for (const campaignId of campaignIds) {
    const key = String(campaignId)
    if (pending.has(key) || loaded.has(key) || failed.has(key) || seen.has(key)) {
      continue
    }
    seen.add(key)
    toRequest.push(campaignId)
  }
  return toRequest
}

async function fetchTargetsChunk(
  dispatch: Dispatch<SponsoredBrandsAction>,
  data: IGetSbTargetBody,
  campaignIds: string[],
  generation: number
) {
  try {
    const targetsData = await sbTargetApi.getTargetData({ ...data, campaignIds })
    dispatch({
      type: SponsoredBrandsActionType.FETCH_SPONSORED_BRANDS_TARGETS_SUCCESS,
      payload: { campaignIds, targets: targetsData.data, generation },
    })
  } catch (e) {
    dispatch({
      type: SponsoredBrandsActionType.FETCH_SPONSORED_BRANDS_TARGETS_ERROR,
      payload: { campaignIds, error: "sponsored brands targets load error", generation },
    })
  }
}

const fetchSponsoredBrandsTargets = (
  data: IGetSbTargetBody,
  options: IFetchTargetsOptions = {}
) => {
  return async (dispatch: Dispatch<SponsoredBrandsAction>, getState: GetState) => {
    const campaignIdsToRequest = selectCampaignIdsToRequest(getState, data.campaignIds, options)
    if (campaignIdsToRequest.length === 0) {
      return // everything asked for is already in flight — nothing to dispatch
    }
    const generation = readRequestGeneration(getState)
    dispatch({
      type: SponsoredBrandsActionType.FETCH_SPONSORED_BRANDS_TARGETS,
      payload: { campaignIds: campaignIdsToRequest, generation },
    })
    for (const campaignIds of chunkCampaignIds(campaignIdsToRequest)) {
      if (readRequestGeneration(getState) !== generation) {
        return
      }
      await fetchTargetsChunk(dispatch, data, campaignIds, generation)
    }
  }
}

const updateTargetsLocal = (targets: ISbTargetDataForTable[]) => {
  return async (dispatch: Dispatch<SponsoredBrandsAction>) => {
    dispatch({
      type: SponsoredBrandsActionType.UPDATE_SPONSORED_BRANDS_TARGETS_LOCAL,
      payload: targets,
    })
  }
}

const copyTargetsToTargetsForTable = (targets: ISbTargetDataForTable[]) => {
  return async (dispatch: Dispatch<SponsoredBrandsAction>) => {
    dispatch({
      type: SponsoredBrandsActionType.ADD_SPONSORED_BRANDS_TARGETS,
      payload: targets,
    })
  }
}

const resetSponsoredBrandsTargets = () => {
  return (dispatch: Dispatch<SponsoredBrandsAction>) => {
    dispatch({ type: SponsoredBrandsActionType.RESET_SPONSORED_BRANDS_TARGETS })
  }
}

export default {
  fetchSponsoredBrandsCampaigns,
  fetchSponsoredBrandsTargets,
  updateTargetsLocal,
  copyTargetsToTargetsForTable,
  resetSponsoredBrandsTargets,
}
